import { getPropertyValueByCode } from "src/services/property";
import { arePropertyValuesEqual } from "src/services/property/arePropertyValuesEqual";
import { CaseSummary } from "src/types/case";
import { ClientSchema } from "src/types/clientSchema";
import { Element, ShortElement } from "src/types/element";
import { PropertyValue } from "src/types/property";

import {
  CasePropertyDiff,
  PropertyPath,
  PropertyChangeType,
} from "./AlertCaseVersionsDiffModal.types";

const MAX_RECURSION_DEPTH = 10;

export function getCaseVersionsPropertyDiffsFromSchema<
  TVersions extends Record<string, CaseSummary | null>
>(
  originalVersion: CaseSummary,
  versions: TVersions,
  clientSchema: ClientSchema,
  versionOrder?: (keyof TVersions)[]
): CasePropertyDiff[] {
  // Find the case type schema for the original version
  const caseTypeSchema = clientSchema.caseTypes.find((ct) => ct.code === originalVersion.typeCode);

  if (!caseTypeSchema) {
    console.warn(`Schema not found for case type: ${originalVersion.typeCode}`);
    return [];
  }

  const diffs: CasePropertyDiff<keyof TVersions>[] = [];

  // Compare root-level properties
  for (const schemaProperty of caseTypeSchema.properties) {
    const originalProperty = getPropertyValueByCode(
      originalVersion.properties,
      schemaProperty.code
    );

    const versionsProperties = {} as Record<keyof TVersions, PropertyValue | null>;
    for (const key in versions) {
      const caseData = versions[key];
      versionsProperties[key] = caseData
        ? getPropertyValueByCode(caseData.properties, schemaProperty.code)
        : null;
    }

    const diff = createPropertyDiff(
      [schemaProperty.code] satisfies PropertyPath,
      originalProperty,
      versionsProperties,
      null,
      null,
      versionOrder
    );

    if (diff) {
      diffs.push(diff);
    }
  }

  // Compare elements recursively
  // Note: CaseSummary.elements is Element[], but we need to handle them as elements with sub-elements
  const versionsElements = {} as Record<keyof TVersions, ShortElement[]>;
  for (const key in versions) {
    const caseData = versions[key];
    versionsElements[key] = (caseData ? caseData.elements : []) as unknown as ShortElement[];
  }
  const elementDiffs = compareElementsRecursively(
    originalVersion.elements as unknown as ShortElement[],
    versionsElements,
    clientSchema,
    new Set(),
    0,
    undefined,
    versionOrder
  );

  diffs.push(...elementDiffs);

  return diffs;
}

function compareElementsRecursively<TVersions extends Record<string, CaseSummary | null>>(
  originalElements: ShortElement[],
  versionsElements: Record<keyof TVersions, ShortElement[]>,
  clientSchema: ClientSchema,
  visitedTypes: Set<string>,
  depth: number,
  parentElement: ShortElement | null = null,
  versionOrder?: (keyof TVersions)[]
): CasePropertyDiff<keyof TVersions>[] {
  const diffs: CasePropertyDiff<keyof TVersions>[] = [];

  // Prevent stack overflow
  if (depth >= MAX_RECURSION_DEPTH) {
    console.warn(`Maximum recursion depth (${MAX_RECURSION_DEPTH}) reached`);
    return diffs;
  }

  // Get all unique element type codes present in all versions
  const allElementTypeCodes = new Set<string>();
  originalElements.forEach((e) => allElementTypeCodes.add(e.configurableType.code));
  Object.values(versionsElements).forEach((elements) => {
    elements.forEach((e) => allElementTypeCodes.add(e.configurableType.code));
  });

  for (const typeCode of allElementTypeCodes) {
    // Prevent infinite loop for circular references
    if (visitedTypes.has(typeCode)) {
      console.warn(`Circular reference detected for element type: ${typeCode}`);
      continue;
    }

    // Find all elements of this type in all versions
    const originalElementsOfType = originalElements.filter(
      (e) => e.configurableType.code === typeCode
    );
    const versionsElementsOfType = {} as Record<keyof TVersions, ShortElement[]>;
    (Object.keys(versionsElements) as (keyof TVersions)[]).forEach((key) => {
      versionsElementsOfType[key] = versionsElements[key].filter(
        (e) => e.configurableType.code === typeCode
      );
    });

    // Get schema definition for this element type
    const elementTypeSchema = clientSchema.elementTypes.find((et) => et.code === typeCode);

    if (!elementTypeSchema) {
      console.warn(`Schema not found for element type: ${typeCode}`);
      continue;
    }

    // Compare each pair of elements (handle multiple elements of same type)
    const maxLength = Math.max(
      originalElementsOfType.length,
      ...Object.values(versionsElementsOfType).map((elements) => elements.length)
    );

    for (let i = 0; i < maxLength; i++) {
      const originalElement = originalElementsOfType[i];
      const versionsElement = {} as Record<keyof TVersions, ShortElement | undefined>;
      for (const key in versionsElementsOfType) {
        versionsElement[key] = versionsElementsOfType[key][i];
      }

      // Create new visited set for this branch
      const newVisitedTypes = new Set(visitedTypes);
      newVisitedTypes.add(typeCode);

      // Compare properties at this element level
      if (originalElement || Object.values(versionsElement).some((e) => e !== undefined)) {
        for (const schemaProperty of elementTypeSchema.properties) {
          const originalProperty = originalElement
            ? getPropertyValueByCode(originalElement.properties, schemaProperty.code)
            : null;

          const versionsProperties = {} as Record<keyof TVersions, PropertyValue | null>;
          for (const key in versionsElement) {
            const element = versionsElement[key];
            versionsProperties[key] = element
              ? getPropertyValueByCode(element.properties, schemaProperty.code)
              : null;
          }

          const diff = createPropertyDiff(
            [typeCode, schemaProperty.code] satisfies PropertyPath,
            originalProperty,
            versionsProperties,
            parentElement,
            originalElement || null,
            versionOrder
          );

          if (diff) {
            diffs.push(diff);
          }
        }

        // Recursively compare sub-elements
        // Note: ShortElement doesn't have elements, but Element does
        // We need to check if the element is actually an Element type
        const originalSubElements =
          originalElement && "elements" in originalElement
            ? (originalElement as unknown as Element).elements
            : [];
        const versionsSubElements = {} as Record<keyof TVersions, ShortElement[]>;
        for (const key in versionsElement) {
          const element = versionsElement[key];
          versionsSubElements[key] =
            element && "elements" in element ? (element as unknown as Element).elements : [];
        }

        if (
          originalSubElements.length > 0 ||
          Object.values(versionsSubElements).some((elements) => elements.length > 0)
        ) {
          const subDiffs = compareElementsRecursively(
            originalSubElements,
            versionsSubElements,
            clientSchema,
            newVisitedTypes,
            depth + 1,
            originalElement || null,
            versionOrder
          );

          // Update subElement references in the sub-diffs
          subDiffs.forEach((subDiff) => {
            // Find the corresponding sub-element for each version
            Object.keys(subDiff.versions).forEach((key) => {
              const version = subDiff.versions[key as keyof typeof subDiff.versions];
              if (version && version.property) {
                // Find the sub-element that contains this property
                const versionElement = versionsElement[key as keyof typeof versionsElement];
                if (versionElement && "elements" in versionElement) {
                  const subElement = (versionElement as unknown as Element).elements.find((se) =>
                    se.properties.some((p) => p.property.code === version.property?.property.code)
                  );
                  if (subElement) {
                    version.subElement = subElement;
                  }
                }
                // Also set for original
                if (originalElement && "elements" in originalElement) {
                  const originalSubElement = (originalElement as unknown as Element).elements.find(
                    (se) =>
                      se.properties.some(
                        (p) => p.property.code === subDiff.original.property?.property.code
                      )
                  );
                  if (originalSubElement) {
                    subDiff.original.subElement = originalSubElement;
                  }
                }
              }
            });
          });

          // Update paths to include parent element type and update element references
          subDiffs.forEach((subDiff) => {
            if (subDiff.path.length === 2) {
              // Convert [subElementType, propertyCode] to [elementType, subElementType, propertyCode]
              subDiff.path = [typeCode, subDiff.path[0], subDiff.path[1]] satisfies PropertyPath;
              subDiff.id = subDiff.path.join(".");
              subDiff.original.element = originalElement || null;
              // Update element and subElement references in versions
              Object.keys(subDiff.versions).forEach((key) => {
                const version = subDiff.versions[key as keyof typeof subDiff.versions];
                if (version) {
                  version.element = originalElement || null;
                  // subElement is already set correctly from the recursive call
                }
              });
            }
          });

          diffs.push(...subDiffs);
        }
      }
    }
  }

  return diffs;
}

function createPropertyDiff<TVersions extends Record<string, CaseSummary | null>>(
  path: PropertyPath,
  originalProperty: PropertyValue | null,
  versionsProperties: Record<keyof TVersions, PropertyValue | null>,
  element: ShortElement | null,
  subElement: ShortElement | null,
  versionOrder?: (keyof TVersions)[]
): CasePropertyDiff<keyof TVersions> | null {
  // Determine change types for each version
  // For sequential comparison: created -> validation -> current
  // Each version is compared with the previous one in the sequence
  const versionsWithChangeType = {} as Record<
    keyof TVersions,
    {
      element: ShortElement | null;
      property: PropertyValue | null;
      subElement: ShortElement | null;
      changeType?: PropertyChangeType;
    } | null
  >;

  let hasChanges = false;

  // Process all versions with sequential comparison
  // Each version is compared with the previous one in the sequence
  // If versionOrder is provided, use it; otherwise use the order of keys in versionsProperties
  const order = versionOrder || (Object.keys(versionsProperties) as (keyof TVersions)[]);

  for (const key of order) {
    if (!(key in versionsProperties)) continue;

    const versionProperty = versionsProperties[key];

    // Determine the base property for comparison (sequential: compare with previous version)
    let baseProperty: PropertyValue | null = originalProperty;

    // For sequential comparison: each version is compared with the previous one
    // Find the index of current key in the order
    const currentIndex = order.indexOf(key);
    if (currentIndex > 0) {
      // Compare with the previous version in the order
      const previousKey = order[currentIndex - 1];
      const previousProperty = versionsProperties[previousKey];
      baseProperty = previousProperty ?? originalProperty;
    }
    // For the first version in order, compare with creation (originalProperty)
    // (already set as default)

    if (!versionProperty && !baseProperty) {
      versionsWithChangeType[key] = null; // Both absent, skip
      continue;
    }

    const changeType: PropertyChangeType =
      !baseProperty && versionProperty
        ? "addition"
        : baseProperty && !versionProperty
        ? "deletion"
        : baseProperty && versionProperty
        ? arePropertyValuesEqual(baseProperty, versionProperty)
          ? "unchanged"
          : "modification"
        : "unchanged";

    // Only return if there's a change (addition, deletion, or modification)
    if (changeType === "unchanged") {
      versionsWithChangeType[key] = null;
    } else {
      hasChanges = true;
      versionsWithChangeType[key] = {
        element: element,
        subElement: subElement,
        property: versionProperty,
        changeType,
      };
    }
  }

  if (!hasChanges && !originalProperty) {
    // Both absent in all versions, skip
    return null;
  }

  return {
    id: path.join("."),
    path,
    original: {
      element: element,
      subElement: subElement,
      property: originalProperty,
    },
    versions: versionsWithChangeType as Record<
      keyof TVersions,
      {
        element: ShortElement | null;
        property: PropertyValue | null;
        subElement: ShortElement | null;
        changeType?: PropertyChangeType;
      } | null
    >,
  };
}
